import React from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';

import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import BudgetConsolidationCardsPanel from './BudgetConsolidationCardsPanel';
import BudgetControlSummaryPanel from './BudgetControlSummaryPanel';
import BudgetConsolidationAccountGrid from './BudgetConsolidationAccountGrid';
import BudgetConsolidationCostCenterGrid from './BudgetConsolidationCostCenterGrid';
import BudgetArbitrationGrid from './BudgetArbitrationGrid';

import {
  BudgetConsolidationSource,
  BudgetConsolidationWorkbenchModel,
} from './budgetconsolidation.models';

import {
  fetchBudgetArbitrations,
  fetchBudgetConsolidationAccount,
  fetchBudgetConsolidationCards,
  fetchBudgetConsolidationCostCenter,
  fetchBudgetConsolidationWorkbench,
  fetchBudgetControlAreas,
  returnBudgetConsolidationSource,
  reopenBudgetConsolidation,
  saveBudgetArbitrations,
  transitionBudgetConsolidation,
} from './budgetconsolidation.apis';

import {
  PlanningPageProps,
  PlanningWorkbenchShell,
} from '../planningcore';

import {
  fetchPlanningJourneyPhases,
} from '../core/planningJourney.cache';

import FPlanningJourney from '../core/FPlanningJourney';


interface State {
  model: BudgetConsolidationWorkbenchModel | null;

  tab: number;

  loading: boolean;
  saving: boolean;

  error: string;
  success: string;

  phases: any[];

  returnSource: BudgetConsolidationSource | null;
  returnReason: string;
  reopenDialogOpen: boolean;
  reopenReason: string;
}


export default class BudgetConsolidationWorkbench extends React.Component<
  PlanningPageProps,
  State
> {

  state: State = {
    model: null,

    tab: 0,

    loading: false,
    saving: false,

    error: '',
    success: '',

    phases: [],

    returnSource: null,
    returnReason: '',
    reopenDialogOpen: false,
    reopenReason: '',
  };


  componentDidMount() {
    void this.initialize();
  }


  private pci = () =>
    Number(this.props.plancontext?.pci || 0);


  /* ============================================================
     INITIALIZATION
     ============================================================ */

  private initialize = async () => {

    const pci = this.pci();

    if (!pci) {

      this.setState({
        error: 'Planning Cycle context is missing.',
      });

      return;
    }


    this.setState({
      loading: true,
      error: '',
      success: '',
    });


    try {

      const phases =
        await fetchPlanningJourneyPhases();

      let model: BudgetConsolidationWorkbenchModel;


      /*
       * Preferred PG-01D contract:
       *
       * One authoritative workbench response containing:
       * - context
       * - cards
       * - sources
       * - controlareas
       * - accounts
       * - costcenters
       * - arbitrations
       * - actions
       */
      try {

        model =
          await fetchBudgetConsolidationWorkbench(pci);

      } catch {

        /*
         * Compatibility fallback.
         *
         * Source lifecycle authority is deliberately unavailable
         * here because the legacy endpoints do not expose the new
         * PG-01D source header contract.
         *
         * Consequently COMPLETE remains disabled.
         */

        const [
          cards,
          accounts,
          costcenters,
          controlareas,
          arbitrations,
        ] = await Promise.all([

          fetchBudgetConsolidationCards(pci),

          fetchBudgetConsolidationAccount(pci),

          fetchBudgetConsolidationCostCenter(pci),

          fetchBudgetControlAreas(pci)
            .catch(() => []),

          fetchBudgetArbitrations(pci)
            .catch(() => []),

        ]);


        model = {

          context: {
            planningcycleid: pci,

            planningcyclecode:
              this.props.plancontext?.pcc || '',

            planningcyclename:
              this.props.plancontext?.pcn || '',

            budgetperiodid:
              Number(
                this.props.plancontext?.bpi || 0
              ),

            budgetperiodcode:
              this.props.plancontext?.bpc || '',

            budgetperiodname:
              this.props.plancontext?.bpn || '',

            phasecode:
              'BUDGET CONSOLIDATION',
          },


          cards: {
            ...cards,

            authoritativesources: 0,
            requiredsources: 3,
            allsourcesauthoritative: false,
          },


          /*
           * Legacy fallback cannot safely infer lifecycle.
           */
          sources: [],


          accounts,
          costcenters,
          controlareas,
          arbitrations,


          actions: {
            canRefresh: true,

            canArbitrate: true,
            canSaveArbitration: true,

            financiallyReady: false,
            allSourcesAuthoritative: false,

            canComplete: false,
            canReopen: false,
            canExit: false,

            nextpage: null,
          },

        };

      }


      this.setState({
        model,
        phases,
      });

    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          String(error),
      });

    } finally {

      this.setState({
        loading: false,
      });

    }

  };


  /* ============================================================
     ARBITRATION
     ============================================================ */

  private saveArbitration = async (
    records: any[]
  ) => {

    this.setState({
      saving: true,
      error: '',
      success: '',
    });


    try {

      await saveBudgetArbitrations(
        this.pci(),
        records
      );


      this.setState({
        success:
          'Arbitration decisions saved.',
      });


      await this.initialize();

    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          String(error),
      });

      throw error;

    } finally {

      this.setState({
        saving: false,
      });

    }

  };


  /* ============================================================
     CONSOLIDATION COMPLETE
     ------------------------------------------------------------
     Consolidation owns its own COMPLETE lifecycle.

     It no longer owns a generic/global RETURN action.
     Upstream correction is performed through targeted source
     RETURN below.
     ============================================================ */

  private completeConsolidation = async () => {

    const model =
      this.state.model;

    if (!model) {
      return;
    }


    if (!model.actions?.canComplete) {

      this.setState({
        error:
          'Budget Consolidation is not currently qualified for completion.',
      });

      return;
    }


    this.setState({
      saving: true,
      error: '',
      success: '',
    });


    try {

      await transitionBudgetConsolidation(
        this.pci(),
        model.context.stamp
      );


      this.setState({
        success:
          'Budget Consolidation completed.',
      });


      await this.initialize();

    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          String(error),
      });

    } finally {

      this.setState({
        saving: false,
      });

    }

  };


  /* ============================================================
     PG-02A CONSOLIDATION REOPEN
     ============================================================ */

  private openReopenDialog = () => {
    if (!this.state.model?.actions?.canReopen || this.state.saving) return;
    this.setState({ reopenDialogOpen: true, reopenReason: '', error: '' });
  };

  private closeReopenDialog = () => {
    if (this.state.saving) return;
    this.setState({ reopenDialogOpen: false, reopenReason: '' });
  };

  private confirmReopen = async () => {
    const model = this.state.model;
    if (!model?.actions?.canReopen || this.state.saving) return;

    const reason = this.state.reopenReason.trim();
    const voluntary = model.context.statecode?.toUpperCase() === 'COMPLETE';

    if (voluntary && !reason) {
      this.setState({ error: 'A reason is required to reopen completed Consolidation.' });
      return;
    }
    if (!model.context.stamp) {
      this.setState({ error: 'Consolidation stamp is missing. Refresh and try again.' });
      return;
    }

    this.setState({ saving: true, error: '', success: '' });
    try {
      await reopenBudgetConsolidation(this.pci(), model.context.stamp, reason);
      this.setState({ reopenDialogOpen: false, reopenReason: '' });
      await this.initialize();
      this.setState({ success: 'Budget Consolidation reopened for correction.' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };


  /* ============================================================
     PG-01D TARGETED SOURCE RETURN
     ============================================================ */

  private openReturnSource = (
    source: BudgetConsolidationSource
  ) => {

    if (!source?.canreturn) {
      return;
    }


    this.setState({
      returnSource: source,
      returnReason: '',
      error: '',
      success: '',
    });

  };


  private closeReturnSource = () => {

    if (this.state.saving) {
      return;
    }


    this.setState({
      returnSource: null,
      returnReason: '',
    });

  };


  private confirmReturnSource = async () => {

    const source =
      this.state.returnSource;

    const reason =
      this.state.returnReason.trim();


    if (!source) {
      return;
    }


    if (!reason) {

      this.setState({
        error:
          'A reason is required to return a Budget for correction.',
      });

      return;
    }


    if (!source.stamp) {

      this.setState({
        error:
          `${source.sourcename} concurrency stamp is missing. Refresh the workbench and try again.`,
      });

      return;
    }


    this.setState({
      saving: true,
      error: '',
      success: '',
    });


    try {

      await returnBudgetConsolidationSource(
        this.pci(),
        source.sourcecode,
        reason,
        source.stamp
      );


      /*
       * Close the dialog before refreshing.
       */
      this.setState({
        returnSource: null,
        returnReason: '',

        success:
          `${source.sourcename} returned for correction.`,
      });


      await this.initialize();

    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          String(error),
      });

    } finally {

      this.setState({
        saving: false,
      });

    }

  };


  /* ============================================================
     AUTHORITY NOTICE
     ============================================================ */

  private authorityNotice = () => {

    const model =
      this.state.model;

    if (!model) {
      return null;
    }


    const actions =
      model.actions;

    if (actions?.allSourcesAuthoritative) {
      return null;
    }


    const pending =
      (model.sources || [])
        .filter(
          source =>
            !source.authoritative
        );


    /*
     * If the new source contract is unavailable, avoid fabricating
     * lifecycle details.
     */
    if (!pending.length) {

      return (
        <Alert severity="info">
          Consolidation remains visible while source budgets are
          being prepared, but it cannot be completed until all
          required source budgets are submitted.
        </Alert>
      );

    }


    const details =
      pending
        .map(
          source =>
            `${source.sourcename} is ${source.statename}`
        )
        .join('; ');


    return (
      <Alert severity="info">

        Consolidation remains visible while source budgets are
        being prepared, but it cannot be completed until all
        required source budgets are submitted.

        {' '}

        {details}.

      </Alert>
    );

  };


  /* ============================================================
     RENDER
     ============================================================ */

  render() {

    const {
      model,
      returnSource,
      returnReason,
      reopenDialogOpen,
      reopenReason,
    } = this.state;


    const actions =
      model?.actions;


    return (

      <PlanningWorkbenchShell

        {...this.props}

        title={
          this.props.title ||
          'Budget Consolidation & Arbitration'
        }

        subtitle={
          'Consolidate Activity, Direct and Investment budgets, compare them with Budget Control, and record governance arbitration decisions.'
        }

        icon={
          <AccountBalanceOutlinedIcon />
        }

        loading={
          this.state.loading
        }

        saving={
          this.state.saving
        }

        error={
          this.state.error
        }

        success={
          this.state.success
        }

        onRefresh={
          this.initialize
        }


        /* ========================================================
           PLANNING JOURNEY
           ======================================================== */

        headerExtension={

          <FPlanningJourney
            phases={this.state.phases}

            currentPhaseCode={
              model?.context.phasecode ||
              'BUDGET CONSOLIDATION'
            }
          />

        }


        /* ========================================================
           GOVERNANCE ACTIONS
           --------------------------------------------------------
           COMPLETE remains visible even while disabled.

           Global RETURN has intentionally been removed.
           ======================================================== */

        actions={

          <Stack
            direction="row"
            spacing={1}
          >

            {actions?.canReopen && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<ReplayOutlinedIcon />}
                disabled={this.state.saving}
                onClick={this.openReopenDialog}
              >
                Reopen Consolidation
              </Button>
            )}
            <Button
              variant="contained"

              startIcon={
                <DoneAllOutlinedIcon />
              }

              disabled={
                this.state.saving ||
                !actions?.canComplete
              }

              onClick={
                this.completeConsolidation
              }
            >
              Complete Consolidation
            </Button>

          </Stack>

        }

      >

        {!model ? (

          <Alert severity="info">
            No Budget Consolidation context is available.
          </Alert>

        ) : (

          <Stack spacing={3}>


            {/* ====================================================
                ENTERPRISE SUMMARY
                ==================================================== */}

            <BudgetConsolidationCardsPanel
              cards={model.cards}
            />


            {/* ====================================================
                SOURCE / BUDGET CONTROL CARDS

                Existing Budget Control cards are enriched with
                source lifecycle and targeted RETURN actions.
                ==================================================== */}

            <BudgetControlSummaryPanel
              rows={model.controlareas}
              sources={model.sources}
              disabled={this.state.saving}
              onReturnSource={
                this.openReturnSource
              }
            />


            {/* ====================================================
                SOURCE AUTHORITY QUALIFICATION
                ==================================================== */}

            {this.authorityNotice()}


            {/* ====================================================
                FINANCIAL QUALIFICATION WARNINGS
                ==================================================== */}

            {model.cards.unmappedlines > 0 && (

              <Alert severity="warning">

                {model.cards.unmappedlines}{' '}
                unmapped line(s), worth{' '}

                {Number(
                  model.cards.unmappedamount || 0
                ).toLocaleString('fr-FR')}

                , must be resolved before completion.

              </Alert>

            )}


            {model.cards.totalbudget >
              model.cards.approvedcapacity && (

                <Alert severity="warning">

                  Consolidated demand exceeds approved capacity.
                  Arbitration must reduce or reassign the excess
                  before completion.

                </Alert>

              )}


            {/* ====================================================
                CONSOLIDATION ANALYSIS
                ==================================================== */}

            <Tabs

              value={
                this.state.tab
              }

              onChange={
                (_, tab) =>
                  this.setState({ tab })
              }

            >

              <Tab
                label="By Account"
              />

              <Tab
                label="By Cost Center"
              />

              <Tab
                label={
                  `Arbitration (${model.cards.pendingarbitrations || 0
                  })`
                }
              />

            </Tabs>


            {/* ====================================================
                ACCOUNT VIEW
                ==================================================== */}

            {this.state.tab === 0 && (

              <BudgetConsolidationAccountGrid

                rows={
                  model.accounts
                }

                loading={
                  this.state.loading
                }

              />

            )}


            {/* ====================================================
                COST CENTRE VIEW
                ==================================================== */}

            {this.state.tab === 1 && (

              <BudgetConsolidationCostCenterGrid

                rows={
                  model.costcenters
                }

                loading={
                  this.state.loading
                }

              />

            )}


            {/* ====================================================
                ARBITRATION
                ==================================================== */}

            {this.state.tab === 2 && (

              <BudgetArbitrationGrid

                rows={
                  model.arbitrations
                }

                loading={
                  this.state.loading ||
                  this.state.saving
                }

                editable={
                  Boolean(
                    actions?.canArbitrate
                  )
                }

                onRowsChange={
                  arbitrations =>
                    this.setState({
                      model: {
                        ...model,
                        arbitrations,
                      },
                    })
                }

                onSave={
                  this.saveArbitration
                }

              />

            )}

            {/* PG-02A REOPEN DIALOG */}
            <Dialog
              open={reopenDialogOpen}
              onClose={this.closeReopenDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Reopen Budget Consolidation</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
                  Reopening withdraws Consolidation from Validation readiness until
                  it is completed again. Existing budgets and arbitration records
                  are preserved.
                </Alert>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={3}
                  required={model?.context.statecode?.toUpperCase() === 'COMPLETE'}
                  label="Reason for Reopening"
                  value={reopenReason}
                  disabled={this.state.saving}
                  onChange={(event) => this.setState({ reopenReason: event.target.value })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={this.closeReopenDialog} disabled={this.state.saving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={
                    this.state.saving ||
                    !model?.actions?.canReopen ||
                    (model?.context.statecode?.toUpperCase() === 'COMPLETE' && !reopenReason.trim())
                  }
                  onClick={this.confirmReopen}
                >
                  Reopen
                </Button>
              </DialogActions>
            </Dialog>

            {/* ====================================================
                TARGETED SOURCE RETURN DIALOG
                ==================================================== */}

            <Dialog

              open={
                Boolean(returnSource)
              }

              onClose={
                this.closeReturnSource
              }

              fullWidth

              maxWidth="sm"

            >

              <DialogTitle>

                Return{' '}

                {returnSource?.sourcename ||
                  'Budget'}

                {' '}for Correction

              </DialogTitle>


              <DialogContent>

                <Alert
                  severity="warning"
                  sx={{
                    mt: 1,
                    mb: 2,
                  }}
                >

                  This will return only the selected source
                  Budget for correction. Other submitted source
                  budgets and the Consolidation workspace will
                  remain unchanged.

                </Alert>


                {returnSource && (

                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                    }}
                  >

                    {returnSource.sourcename}

                    {' — '}

                    {returnSource.statename}

                  </Alert>

                )}


                <TextField

                  autoFocus

                  fullWidth

                  required

                  multiline

                  minRows={3}

                  label="Reason for Return"

                  value={
                    returnReason
                  }

                  disabled={
                    this.state.saving
                  }

                  onChange={
                    event =>
                      this.setState({
                        returnReason:
                          event.target.value,
                      })
                  }

                />

              </DialogContent>


              <DialogActions>

                <Button

                  disabled={
                    this.state.saving
                  }

                  onClick={
                    this.closeReturnSource
                  }

                >
                  Cancel
                </Button>


                <Button

                  variant="contained"

                  color="warning"

                  disabled={
                    this.state.saving ||
                    !returnReason.trim()
                  }

                  onClick={
                    this.confirmReturnSource
                  }

                >
                  Return for Correction
                </Button>

              </DialogActions>

            </Dialog>

          </Stack>

        )}

      </PlanningWorkbenchShell>

    );

  }

}
